/**
 * The unified diff a transform reply shows (slate-feature-transfer W8 §B.9 / S10).
 *
 * Two sources, one shape. The server sometimes stores a `diff` string in the transform
 * message's metadata; a transform that has just run returns only the NEW text. Rather than
 * render one of them richly and the other as a blob, both become `DiffLine[]` and the viewer
 * renders that — so a reply from three weeks ago and one from three seconds ago look alike.
 *
 * Why a diff at all, when the reply also carries the whole rewritten text: because "accept
 * this rewrite" is a decision about what CHANGED, and a person asked to approve prose they
 * must re-read in full will approve it without reading. The diff is the review surface.
 */

export type DiffKind = "add" | "del" | "context" | "header";

export interface DiffLine {
  kind: DiffKind;
  text: string;
}

/** Recognise the shapes a unified diff uses for something that is not content. */
function headerLine(line: string): boolean {
  return (
    line.startsWith("@@") ||
    line.startsWith("diff ") ||
    line.startsWith("+++") ||
    line.startsWith("---") ||
    line.startsWith("index ")
  );
}

export function parseUnifiedDiff(diff: string): DiffLine[] {
  return diff.split("\n").map((line) => {
    if (headerLine(line)) {
      return { kind: "header" as const, text: line };
    }
    if (line.startsWith("+")) {
      return { kind: "add" as const, text: line.slice(1) };
    }
    if (line.startsWith("-")) {
      return { kind: "del" as const, text: line.slice(1) };
    }
    return { kind: "context" as const, text: line.startsWith(" ") ? line.slice(1) : line };
  });
}

/**
 * A line-level diff, longest-common-subsequence.
 *
 * Deliberately line-level and not word-level: a transform rewrites sentences, and a word diff
 * of two rewritten sentences is confetti. It is also O(n·m) in LINES, which for a selection —
 * the only thing this is ever called on — is a handful either way.
 */
export function lineDiff(before: string, after: string): DiffLine[] {
  const left = before.split("\n");
  const right = after.split("\n");

  // table[i][j] = length of the LCS of left[i:] and right[j:]
  const table: number[][] = Array.from({ length: left.length + 1 }, () =>
    new Array<number>(right.length + 1).fill(0),
  );
  for (let i = left.length - 1; i >= 0; i -= 1) {
    for (let j = right.length - 1; j >= 0; j -= 1) {
      const row = table[i] as number[];
      const next = table[i + 1] as number[];
      row[j] =
        left[i] === right[j]
          ? (next[j + 1] as number) + 1
          : Math.max(next[j] as number, row[j + 1] as number);
    }
  }

  const lines: DiffLine[] = [];
  let i = 0;
  let j = 0;
  while (i < left.length && j < right.length) {
    if (left[i] === right[j]) {
      lines.push({ kind: "context", text: left[i] as string });
      i += 1;
      j += 1;
    } else if ((table[i + 1]?.[j] ?? 0) >= (table[i]?.[j + 1] ?? 0)) {
      lines.push({ kind: "del", text: left[i] as string });
      i += 1;
    } else {
      lines.push({ kind: "add", text: right[j] as string });
      j += 1;
    }
  }
  for (; i < left.length; i += 1) {
    lines.push({ kind: "del", text: left[i] as string });
  }
  for (; j < right.length; j += 1) {
    lines.push({ kind: "add", text: right[j] as string });
  }
  return lines;
}

/** Whichever source exists, in the viewer's one shape. */
export function diffLines(input: {
  diff?: string | null;
  before?: string | null;
  after?: string | null;
}): DiffLine[] {
  if (input.diff?.trim()) {
    return parseUnifiedDiff(input.diff);
  }
  if (typeof input.after === "string") {
    return lineDiff(input.before ?? "", input.after);
  }
  return [];
}
